import React, { useEffect, useId, useState } from 'react';
import { useParams } from 'react-router-dom';
import { API } from '../data/API/API';
import { IMemberInvoiceReview, ReviewStatus } from '../data/API/MemberInvoiceReview';
import MemberInvoiceTable from '../components/MemberInvoiceTable/MemberInvoiceTable';
import FilePickerTable from '../components/FilePickerTable/FilePickerTable';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faExclamation, faArrowLeft, faPaperPlane, faCheck, faFile, faBan, faFilePdf, faCheckCircle, faTimesCircle } from '@fortawesome/free-solid-svg-icons';
import { Modal } from 'react-bootstrap';
import './MemberInvoiceReviewPage.scss';
import Spinner from 'react-bootstrap/Spinner';
import FormControl from 'react-bootstrap/FormControl';
import { Infobar } from '../components/Infobar/Infobar';

enum PageState {
    BLANK,
    DEFAULT,
    DEVIATION,
    SENT
}

const MemberInvoiceReviewPage = () => {
    const fileInputId = useId();
    const { key } = useParams<{ key?: string }>();
    const [pageData, setPageData] = useState<IMemberInvoiceReview>({
        provider: '',
        providerName: '',
        customer: '',
        period: new Date(),
        endPeriod: new Date(), // Add endPeriod to initial state
        status: ReviewStatus.NEW,
        invoices: []
    });

    const [fileList, setFileList] = useState<File[]>([]);
    const [pageState, setPageState] = useState<PageState>(PageState.BLANK);
    const [isLoading, setLoading] = useState<boolean>(false);
    const [showApproveModal, setShowApproveModal] = useState(false);
    const [showConfirmModal, setShowConfirmModal] = useState(false); // Confirmation modal for sending files
    const [isSendingFiles, setIsSendingFiles] = useState(false); // Track sending state
    const [comment, setComment] = useState<string>("");
    const [sendWithoutFiles, setSendWithoutFiles] = useState<boolean>(false); // State for checkbox
    const [amountCorrect, setAmountCorrect] = useState<boolean | null>(null);
    const [quantityCorrect, setQuantityCorrect] = useState<boolean | null>(null);
    const [labelCorrect, setLabelCorrect] = useState<boolean | null>(null);


    useEffect(() => {
        const fetchData = async () => {
            if (key === undefined) return null;

            try {
                setLoading(true);
                const data = await API.MemberInvoiceReview.GetByKey(key);

                // If the status is either NEW or REOPENED, transition it to OPENED
                if (data.status === ReviewStatus.NEW) {
                    data.status = ReviewStatus.OPENED;
                    await postStatusUpdate(key, ReviewStatus.OPENED, comment, false); // No correctness flags
                }

                setPageData(data);
                setPageState(PageState.DEFAULT);
                setLoading(false);
            } catch (e) {
                window.location.href = "/";
            }
        };

        fetchData();
    }, [key]);

    const postStatusUpdate = async (
        key: string,
        status: ReviewStatus,
        comment: string,
        hasFiles: boolean,
        amountCorrect?: boolean | null,
        quantityCorrect?: boolean | null,
        labelCorrect?: boolean | null
    ) => {
        setLoading(true);

        try {
            console.log('PostStatus', key, status, comment, hasFiles, amountCorrect, quantityCorrect, labelCorrect);
            await API.MemberInvoiceReview.PostStatus(
                key,
                status,
                comment,
                hasFiles,
                amountCorrect,
                quantityCorrect,
                labelCorrect
            );
        } catch (e) {
            console.error(`Something went wrong with the status update.`, e);
        }

        setLoading(false);
    };

    const postFiles = async (key: string, fileList: File[]) => {
        setLoading(true);

        try {
            await API.MemberInvoiceReview.PostFiles(key, fileList);
        } catch (e) {
            console.error(`Something went wrong with the file upload.`, e);
        }

        setLoading(false);
    }

    const onBackClick = async () => {
        if (key === undefined) return;
        setPageState(PageState.DEFAULT);
    };

    const onApproveSendClick = async () => {
        if (key === undefined) return;
        setShowApproveModal(true);
    };

    const handleApprove = async () => {
        if (pageData.status !== ReviewStatus.APPROVED) {
            setPageData({ ...pageData, status: ReviewStatus.APPROVED });
            await postStatusUpdate(
                key!,
                ReviewStatus.APPROVED,
                comment,
                false,
                amountCorrect,
                quantityCorrect,
                labelCorrect
            );
        }
        setShowApproveModal(false);
        setPageState(PageState.SENT);
    };


    // Directly switch to file upload view on deviation
    const onDeviationClick = () => {
        setPageState(PageState.DEVIATION); // Switch to the file upload view directly
    };

    const handleSendWithoutFiles = async () => {
        await handleDeviationSend(); // Send without files
    };

    const handleDeviationSend = async () => {
        if (fileList.length > 0) {
            if (pageData.status !== ReviewStatus.DEVIATION) {
                setPageData({ ...pageData, status: ReviewStatus.DEVIATION });
                await postStatusUpdate(
                    key!,
                    ReviewStatus.DEVIATION,
                    comment,
                    true,
                    amountCorrect,
                    quantityCorrect,
                    labelCorrect
                );
                await postFiles(key!, fileList);
            }
        } else {
            if (pageData.status !== ReviewStatus.DEVIATION) {
                setPageData({ ...pageData, status: ReviewStatus.DEVIATION });
                await postStatusUpdate(
                    key!,
                    ReviewStatus.DEVIATION,
                    comment,
                    false,
                    amountCorrect,
                    quantityCorrect,
                    labelCorrect
                );
            }
        }
        setPageState(PageState.SENT);
    };


    const handleConfirmSend = async () => {
        setIsSendingFiles(true);
        await handleDeviationSend();
        setShowConfirmModal(false);
        setIsSendingFiles(false);
    };

    const onSelectFile = (event: React.ChangeEvent<HTMLInputElement>) => {
        const { files } = event.target;
        if (files === null) return;

        // Create a list of file names already in the fileList
        const existingFileNames = fileList.map(file => file.name);

        // Filter out files that are already in the fileList by comparing the name
        const newFiles = Array.from(files).filter(file => !existingFileNames.includes(file.name));
        setSendWithoutFiles(false);
        setFileList([...fileList, ...newFiles]);

        // Reset the file input element to allow adding the same file again later
        event.target.value = '';
    };

    const onRemoveFile = (file: File) => {
        setFileList(fileList.filter(f => f !== file));
    };

    const renderReviewList = () => {
        return (
            <div>
                <div className='table-container'>
                    <h2>Rapporterade fakturor</h2>
                    <MemberInvoiceTable invoiceData={pageData?.invoices || []} isLoading={false} />
                </div>
                <div className='button-grid-container'>
                    <button className='button-danger common-button btn' onClick={onDeviationClick}>
                        <FontAwesomeIcon icon={faBan} /> Avvikande rapport
                    </button>
                    <button className='common-button btn' onClick={onApproveSendClick}><FontAwesomeIcon icon={faCheck} /> Godkänn</button>
                </div>
            </div>
        );
    };

    const renderFileList = () => {
        return (
            <div>
                {/* Row for the title, file upload button, and cancel button */}
                <div className="file-header-row" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <h2>Filer</h2>
                    <div className="file-header-buttons" style={{ display: 'flex', gap: '10px' }}>
                        <button className='button-cancel common-button btn' onClick={onBackClick}>
                            <FontAwesomeIcon icon={faArrowLeft} /> Ångra
                        </button>
                        <label className='upload-button common-button btn'>
                            <input id={fileInputId} type="file" onChange={onSelectFile} accept="application/pdf" multiple />
                            <FontAwesomeIcon icon={faFile} /> Välj filer
                        </label>
                    </div>
                </div>

                {/* Table for the files */}
                <div className='table-container' style={{ marginTop: '10px' }}>
                    <FilePickerTable files={fileList} onRemoveFile={onRemoveFile} isLoading={false} />
                </div>

                {/* Grid for checkbox and Granska button with added margin and alignment */}
                <div className='button-grid-container-extra' style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: '10px', marginTop: '20px' }}>
                    {/* Checkbox for sending without files */}
                    <div className='send-without-files-container' style={{ display: 'flex', alignItems: 'center' }}>
                        <label className="send-without-files-label" style={{ display: 'flex', alignItems: 'center' }}>
                            <input
                                type="checkbox"
                                className="send-without-files-checkbox"
                                checked={sendWithoutFiles}
                                onChange={(e) => setSendWithoutFiles(e.target.checked)}
                                disabled={fileList.length > 0} // Disable if files have been added
                                style={{ marginRight: '5px', marginTop: '-15px' }} // Space between checkbox and text
                            />
                            <p style={{ fontSize: '18px', color: fileList.length > 0 ? 'gray' : 'black' }}>Skicka utan filer</p>
                        </label>
                    </div>

                    {/* Granska button */}
                    <div
                        title={fileList.length === 0 && !sendWithoutFiles ? "För att fortsätta, vänligen ladda upp filer eller välj 'Skicka utan filer'." : ""}
                    >
                        <button
                            className='common-button button-submit btn'
                            onClick={() => setShowConfirmModal(true)}
                            disabled={fileList.length === 0 && !sendWithoutFiles} // Disable if no files and checkbox not checked
                        >
                            <FontAwesomeIcon icon={faPaperPlane} /> Granska
                        </button>
                    </div>
                </div>
            </div>
        );
    };

    const renderDefault = () => {
        return (
            <>
                <div className="header-section">
                    <h1>{(pageState === PageState.DEFAULT) ? `Granska fakturering` : `Skicka in fakturor`}</h1>
                    <div className='info-grid-container-extra'>
                        <div className='description-box'>
                            <div className='info-left'>
                                <div className='info-box'>
                                    <b>{`${pageData?.providerName}, ${pageData?.provider}`}</b>
                                    <small>Gällande leverantör</small>
                                </div>
                                <div className='info-box'>
                                    <b>
                                        {pageData?.period && pageData?.endPeriod
                                            ? `${pageData.period.toLocaleDateString('sv-SE', { year: 'numeric', month: '2-digit' })} - ${pageData.endPeriod.toLocaleDateString('sv-SE', { year: 'numeric', month: '2-digit' })}`
                                            : 'N/A'}
                                    </b>
                                    <small>För period</small>
                                </div>
                            </div>

                            {/* Descriptive Text Box */}

                            <p><strong>Som en del i företagets kvalitetsarbete genomför vi olika stickprovskontroller av fakturor mellan våra leverantörer och medlemmar. I kontrollerna säkerställs att fakturering skett enligt avtal.</strong></p>
                            <p><strong>Viktiga punkter i fakturorna:</strong></p>
                            <ul>
                                <li>Beloppet på fakturan (att det stämmer med statistiken).</li>
                                <li>Att antal fakturor stämmer överens med vårt system (portalen).</li>
                                <li>Märkningen (Ramavtalsnumret).</li>
                            </ul>
                        </div>
                    </div>
                    {(pageState === PageState.DEFAULT) ? renderReviewList() : renderFileList()}
                </div>
            </>
        );
    };



    const renderSent = () => {
        return (
            <div className='sent-container'>
                <div className='info-container'>
                    <h1>Granskning inskickad</h1>
                    <span>Tack för Erat deltagande.</span>
                </div>
            </div>
        );
    };

    const renderVerify = () => {
        return (
            <div className='verify-container'>
                <h2><FontAwesomeIcon style={{ marginRight: "4px" }} icon={faExclamation} /> Verifierar länk...</h2>
            </div>
        );
    };

    const renderRadioButtonGroup = (
        label: string,
        correctValue: boolean | null,
        setValue: (value: boolean) => void
    ) => (
        <div className="radio-group">
            <label className="radio-label">{label}</label>
            <div className="radio-options">
                <button
                    type="button"
                    className={`radio-button ${correctValue === true ? 'selected-correct' : ''}`}
                    onClick={() => setValue(true)}
                >
                    <FontAwesomeIcon icon={faCheckCircle} className="icon" /> Ja
                </button>
                <button
                    type="button"
                    className={`radio-button ${correctValue === false ? 'selected-incorrect' : ''}`}
                    onClick={() => setValue(false)}
                >
                    <FontAwesomeIcon icon={faTimesCircle} className="icon" /> Nej
                </button>
            </div>
        </div>
    );

    return (
        <div className='member-invoice-review'>
            {isLoading &&
                <div className='loading-container'>
                    <Spinner animation="border" role="status">
                        <span className="visually-hidden">Laddar...</span>
                    </Spinner>
                </div>
            }

            {/* Approve Modal */}
            <Modal show={showApproveModal} onHide={() => setShowApproveModal(false)}>
                <Modal.Header closeButton style={{backgroundColor:'#173c53',color:'white'}}>
                    <Modal.Title>Bekräfta</Modal.Title>
                </Modal.Header>
                <Modal.Body style={{backgroundColor:'#fffaf5'}}>
                    <p>Om ni godkänner kommer granskningen skickas in.</p>
                    {renderRadioButtonGroup("Stämmer beloppet på fakturan?", amountCorrect, setAmountCorrect)}
                    {renderRadioButtonGroup("Stämmer antal fakturor?", quantityCorrect, setQuantityCorrect)}
                    {renderRadioButtonGroup("Stämmer märkningen (Ramavtalsnumret)?", labelCorrect, setLabelCorrect)}
                    <FormControl
                        style={{marginTop: 8}}
                        as="textarea"
                        rows={3}
                        placeholder="Lämna en kommentar (valfritt)"
                        value={comment}
                        onChange={(e) => setComment(e.target.value)}
                    />
                </Modal.Body>
                <Modal.Footer style={{backgroundColor:'#fffaf5'}}>
                    <button className="btn button-cancel" onClick={() => setShowApproveModal(false)}>Avbryt</button>
                    <button className="btn" onClick={handleApprove}>Bekräfta</button>
                </Modal.Footer>
            </Modal>

            {/* Confirmation Modal */}
            <Modal show={showConfirmModal} onHide={() => setShowConfirmModal(false)}>
                <Modal.Header closeButton style={{backgroundColor:'#173c53',color:'white'}}>
                    <Modal.Title>Granska</Modal.Title>
                </Modal.Header>
                <Modal.Body style={{backgroundColor:'#fffaf5'}}>
                    {fileList.length > 0
                        ? "Är du säker på att du vill skicka de valda fakturorna?"
                        : "Är du säker på att du vill skicka en avvikelse utan bifogade filer?"}
                    {fileList.length > 0 && (
                        <div className="file-overview">
                            <h5>Valda fakturor:</h5>
                            <ul style={{ listStyleType: 'none', paddingLeft: 0 }}>
                                {fileList.map((file, index) => (
                                    <li key={index}>
                                        <FontAwesomeIcon icon={faFilePdf} style={{ marginRight: '8px', color: 'red' }} />
                                        {file.name}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}
                    {renderRadioButtonGroup("Stämmer beloppet på fakturan?", amountCorrect, setAmountCorrect)}
                    {renderRadioButtonGroup("Stämmer antal fakturor?", quantityCorrect, setQuantityCorrect)}
                    {renderRadioButtonGroup("Stämmer märkningen (Ramavtalsnumret)?", labelCorrect, setLabelCorrect)}
                    <FormControl
                        style={{marginTop: 8}}
                        required={sendWithoutFiles}
                        as="textarea"
                        rows={3}
                        placeholder={sendWithoutFiles ? "Lämna en kommentar" : "Lämna en kommentar (valfritt)"}
                        value={comment}
                        onChange={(e) => setComment(e.target.value)}
                        isInvalid={comment.length < 1 && sendWithoutFiles}
                        isValid={comment.length > 0 && sendWithoutFiles}
                    />
                    {
                        <FormControl.Feedback type="invalid">{"När du skickar utan filer behöver du lämna en kommentar"}</FormControl.Feedback>
                    }
                </Modal.Body>
                <Modal.Footer style={{backgroundColor:'#fffaf5'}}>
                    <button className='btn button-cancel' onClick={() => setShowConfirmModal(false)}>Avbryt</button>
                    <button disabled={sendWithoutFiles && comment.length < 1} className='btn button-submit' onClick={handleConfirmSend}>
                        <FontAwesomeIcon icon={faPaperPlane} /> Skicka
                    </button>
                </Modal.Footer>
            </Modal>

            {(pageState === PageState.BLANK) ? renderVerify() : (pageState === PageState.SENT) ? renderSent() : renderDefault()}
        </div>
    );
};

export default MemberInvoiceReviewPage;

